import React, { useEffect, useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import SkaterAvatar from "@/skate/components/SkaterAvatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { FORMAT_LABEL } from "@/skate/lib/rank";
import { toast } from "sonner";
import { Swords, Plus, MapPin, Calendar, Check, X } from "lucide-react";

const StatusPill = ({ status }) => {
  const map = {
    PENDING:   { label: "PENDING",   cls: "text-yellow-400 border-yellow-400/40" },
    ACCEPTED:  { label: "ACCEPTED",  cls: "text-green-400 border-green-400/40"  },
    DECLINED:  { label: "DECLINED",  cls: "text-zinc-500 border-zinc-700"       },
    COMPLETED: { label: "COMPLETED", cls: "text-zinc-400 border-zinc-700"       },
    CANCELLED: { label: "CANCELLED", cls: "text-zinc-500 border-zinc-700"       },
  };
  const c = map[status] || map.PENDING;
  return <span className={`font-mono text-[9px] tracking-widest border px-2 py-0.5 ${c.cls}`}>{c.label}</span>;
};

export const Challenges = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [box, setBox] = useState("incoming");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(null); // challenge object
  const [winnerId, setWinnerId] = useState("");
  const [score, setScore] = useState("");
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/challenges?box=${box}`);
      setList(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [box]);

  const act = async (id, action) => {
    try {
      await api.patch(`/challenges/${id}`, { action });
      toast.success(`Challenge ${action.toLowerCase()}ed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openSubmit = (ch) => {
    setSubmitOpen(ch);
    setWinnerId("");
    setScore("");
    setNotes("");
  };

  const submitResult = async () => {
    if (!winnerId) { toast.error("Pick a winner"); return; }
    const loser_id = winnerId === submitOpen.challenger_id ? submitOpen.opponent_id : submitOpen.challenger_id;
    try {
      await api.post("/matches", {
        challenge_id: submitOpen.challenge_id,
        winner_id: winnerId,
        loser_id,
        winner_score: score || null,
        notes,
      });
      toast.success("Result submitted. Awaiting opponent confirmation.");
      setSubmitOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <div data-testid="challenges-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">BATTLES</div>
          <h1 className="font-display text-5xl leading-none">CHALLEN<br/><span className="text-[#FACC15]">GES.</span></h1>
        </div>
        <Button
          onClick={() => navigate("/skate/challenges/new")}
          data-testid="new-challenge-btn"
          className="bg-[#FACC15] hover:bg-[#FDE047] text-black rounded-none h-12 px-4 font-display tracking-widest"
        >
          <Plus className="w-4 h-4 mr-1" /> NEW
        </Button>
      </div>

      {/* Box tabs */}
      <div className="grid grid-cols-3 gap-1 bg-zinc-900 p-1 mb-4">
        {[
          ["incoming", "INCOMING"],
          ["outgoing", "SENT"],
          ["all", "ALL"],
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setBox(k)}
            data-testid={`box-${k}`}
            className={`h-10 font-display tracking-widest text-sm transition-colors ${box === k ? "bg-[#FACC15] text-white" : "text-zinc-400 hover:text-white"}`}
          >{l}</button>
        ))}
      </div>

      <div className="space-y-3">
        {loading && <div className="font-mono text-xs text-zinc-500 tracking-widest">LOADING…</div>}
        {!loading && list.length === 0 && (
          <div className="border border-dashed border-zinc-800 p-10 text-center">
            <Swords className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mb-1">NO CHALLENGES</div>
            <p className="text-sm text-zinc-400">{box === "incoming" ? "No one's challenged you. Yet." : "Tap NEW to challenge a skater."}</p>
          </div>
        )}
        {list.map((c) => {
          const isMine = c.challenger_id === user.user_id;
          const them = isMine ? c.opponent : c.challenger;
          return (
            <div key={c.challenge_id} className="border border-zinc-800 p-4" data-testid={`challenge-${c.challenge_id}`}>
              <div className="flex items-center gap-3">
                <SkaterAvatar user={them} size={48} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-display text-xl">@{them?.username}</span>
                    <StatusPill status={c.status} />
                  </div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-1 tracking-wider">
                    {isMine ? "YOU CHALLENGED" : "CHALLENGED YOU"} • {FORMAT_LABEL[c.format]}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3 text-xs font-mono text-zinc-400">
                <div className="flex items-center gap-2">
                  <MapPin className="w-3 h-3" /> {c.spot_name || c.location}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3" /> {new Date(c.scheduled_at).toLocaleString()}
                </div>
              </div>

              {c.note && <p className="text-sm text-zinc-400 mt-3 italic">"{c.note}"</p>}

              {/* Actions */}
              {c.status === "PENDING" && !isMine && (
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => act(c.challenge_id, "ACCEPT")} data-testid={`accept-${c.challenge_id}`} className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-none font-display tracking-widest h-11">
                    <Check className="w-4 h-4 mr-1" /> ACCEPT
                  </Button>
                  <Button onClick={() => act(c.challenge_id, "DECLINE")} data-testid={`decline-${c.challenge_id}`} variant="ghost" className="flex-1 border border-zinc-700 rounded-none font-display tracking-widest h-11 hover:bg-zinc-900">
                    <X className="w-4 h-4 mr-1" /> DECLINE
                  </Button>
                </div>
              )}
              {c.status === "PENDING" && isMine && (
                <Button onClick={() => act(c.challenge_id, "CANCEL")} data-testid={`cancel-${c.challenge_id}`} variant="ghost" className="w-full mt-4 border border-zinc-800 rounded-none font-display tracking-widest h-10 text-zinc-400">
                  CANCEL CHALLENGE
                </Button>
              )}
              {c.status === "ACCEPTED" && (
                <Button onClick={() => openSubmit(c)} data-testid={`submit-result-${c.challenge_id}`} className="w-full mt-4 bg-[#FACC15] hover:bg-[#FDE047] text-black rounded-none font-display tracking-widest h-11">
                  SUBMIT RESULT
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Submit result dialog */}
      <Dialog open={!!submitOpen} onOpenChange={(o) => !o && setSubmitOpen(null)}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-tight">SUBMIT RESULT</DialogTitle>
          </DialogHeader>
          {submitOpen && (
            <div className="space-y-4">
              <div className="font-mono text-[10px] tracking-widest text-zinc-500">PICK THE WINNER</div>
              <div className="grid grid-cols-2 gap-2">
                {[submitOpen.challenger, submitOpen.opponent].map((p) => (
                  <button
                    key={p?.user_id}
                    onClick={() => setWinnerId(p.user_id)}
                    data-testid={`winner-${p?.user_id}`}
                    className={`border p-3 text-left ${winnerId === p?.user_id ? "border-[#FACC15] bg-[#FACC15]/10" : "border-zinc-800 hover:border-zinc-600"}`}
                  >
                    <div className="flex items-center gap-2">
                      <SkaterAvatar user={p} size={32} />
                      <div>
                        <div className="font-display text-sm">@{p?.username}</div>
                        <div className="font-mono text-[9px] text-zinc-500">{p?.elo} ELO</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div>
                <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-2">SCORE (OPTIONAL)</label>
                <Input
                  data-testid="score-input"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  placeholder="e.g. S-K vs S-K-A-T-E"
                  className="bg-[#121212] border-zinc-800 rounded-none font-mono focus:border-[#FACC15] focus:ring-0"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest text-zinc-500 block mb-2">NOTES</label>
                <Input
                  data-testid="notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Heated session at McBain banks."
                  className="bg-[#121212] border-zinc-800 rounded-none font-mono focus:border-[#FACC15] focus:ring-0"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setSubmitOpen(null)} className="border border-zinc-800 rounded-none font-display tracking-widest">CANCEL</Button>
            <Button onClick={submitResult} data-testid="submit-result-confirm" className="bg-[#FACC15] hover:bg-[#FDE047] text-black rounded-none font-display tracking-widest">SUBMIT</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Challenges;
